import { LuUsers as LuUsers2 } from "react-icons/lu";

import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { CoinCategoryFormatter } from "@/components/shared/coin-category-formatter";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import DesoAvatar from "@/components/shared/deso-avatar";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { UserDisplayNameWithStatus } from "@/components/shared/user-display-name-with-status";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import type { CREATOR_COIN_TYPES } from "@/utils/constants";
import { DESO_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import { DESO_DENOMINATED_TOKENS } from "@/utils/constants";
import { FOCUS_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import { formatDecimalValue, usdToDeso, usdToFocus } from "@/utils/currency";
import { DIALOG } from "@/utils/dialog-routes";
import getContentBlurredStyle from "@/utils/get-content-blurred-style";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";

export const renderDenominatingCurrencyTableCell = ({
  publicKey,
  focusExchangeRate,
  desoExchangeRate,
  priceUsd,
}: {
  publicKey: string;
  focusExchangeRate: number;
  desoExchangeRate: number;
  priceUsd: number;
}) => {
  const isDesoDenominated = DESO_DENOMINATED_TOKENS.includes(publicKey);
  const price = isDesoDenominated
    ? usdToDeso(priceUsd, desoExchangeRate)
    : usdToFocus(priceUsd, focusExchangeRate);
  const denominatingCurrencyDisplayName = isDesoDenominated
    ? DESO_TOKEN_DISPLAY_NAME
    : FOCUS_TOKEN_DISPLAY_NAME;

  return (
    <div
      className="
        font-mono text-xs text-muted
        hover:text-foreground
      "
    >
      <CoinPriceFormatter price={price} />{" "}
      <span className="text-[10px]">{denominatingCurrencyDisplayName}</span>
    </div>
  );
};

export const renderPriceUsdTableCell = (
  publicKey: string,
  priceUsd: number,
  focusExchangeRate: number,
  desoExchangeRate: number,
) => {
  return (
    <div className="flex flex-col gap-0 text-left">
      <div className="flex items-center font-mono text-muted-foreground">
        <CoinPriceFormatter className="text-sm" price={priceUsd} />
      </div>
      {renderDenominatingCurrencyTableCell({
        publicKey,
        focusExchangeRate,
        desoExchangeRate,
        priceUsd,
      })}
    </div>
  );
};

export const renderCoinUserTableCell = ({
  coinUsername,
  coinPublicKey,
  extraData,
  clickable,
  coinCategory,
  usernamePrefix = "$",
  isTokenCreator = false,
  footerText,
  avatarSize = 40,
  userNameClassName,
}: {
  coinUsername: string;
  coinPublicKey: string;
  extraData?: Record<string, string>;
  clickable: boolean;
  coinCategory?: CREATOR_COIN_TYPES | string | null;
  usernamePrefix?: string;
  isTokenCreator?: boolean;
  footerText?: string;
  avatarSize?: number;
  userNameClassName?: string;
}) => {
  const _internalClickable =
    clickable && coinPublicKey !== process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY;

  return (
    <div className="flex w-full items-center gap-2 text-xs">
      <DesoAvatar
        clickable={_internalClickable}
        size={avatarSize}
        account={{
          publicKey: coinPublicKey,
          username: coinUsername,
          extraData,
        }}
      />
      <div
        className="flex grow flex-col items-start gap-0"
        style={getContentBlurredStyle(
          coinUsername?.length || coinPublicKey?.length,
        )}
      >
        <div className="inline-flex w-full text-ellipsis">
          <UserDisplayNameWithStatus
            whaleSize={16}
            ammIconSize={20}
            clickable={_internalClickable}
            {...{
              username: coinUsername ?? "",
              publicKey: coinPublicKey ?? "",
              extraData,
              usernamePrefix,
              className: cn(
                `
                  w-[80px] grow truncate overflow-hidden pl-0 text-left
                  font-sans text-sm underline-offset-4
                  lg:w-[80px]
                  xl:w-[120px]
                `,
                userNameClassName,
              ),
            }}
          />
        </div>

        {isTokenCreator && (
          <InfoTooltip
            text={
              <span className="text-xs text-muted">
                You are the owner of this token
              </span>
            }
          >
            <Badge className="my-1 text-[10px]">Created By You</Badge>
          </InfoTooltip>
        )}

        {coinCategory && (
          <CoinCategoryFormatter
            coinCategory={coinCategory}
            className="text-muted"
          />
        )}

        {footerText && <span className="text-muted">{footerText}</span>}
      </div>
    </div>
  );
};

export const renderMarketCapTableCell = ({
  publicKey,
  marketCapUsd,
  focusExchangeRate,
  desoExchangeRate,
}: {
  publicKey: string;
  marketCapUsd: number;
  focusExchangeRate: number;
  desoExchangeRate: number;
}) => {
  return (
    <div className="flex flex-col gap-0 text-left font-mono text-foreground">
      <div className="text-sm">${abbreviateNumber(marketCapUsd)}</div>

      {renderDenominatingCurrencyTableCell({
        publicKey,
        focusExchangeRate,
        desoExchangeRate,
        priceUsd: marketCapUsd,
      })}
    </div>
  );
};

export const renderMarketCapTooltip = () => {
  return (
    <InfoTooltip
      iconSize={16}
      text="Market cap is simply computed as price times the total number of coins in
circulation. Note that locked coins are included in the supply in the top
row, but not in the second row."
    />
  );
};
interface HoldersCellProps {
  row: {
    original: {
      coinPublicKey?: string;
      coinUsername?: string;
    };
    getValue: (key: string) => number;
  };
  coinUsername?: string;
}

const HoldersCell = ({ row, coinUsername }: HoldersCellProps) => {
  const openHolders = () =>
    setDialog(DIALOG.Holders, {
      open: true,
      params: {
        publicKey: row.original.coinPublicKey ?? "",
        username: coinUsername ?? row.original.coinUsername ?? "",
      },
    });

  return (
    <div
      className="
        ml-auto flex cursor-pointer items-center justify-end gap-2 pr-3
        text-right
        hover:opacity-80
      "
      onClick={openHolders}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openHolders();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="font-mono text-sm">
        {abbreviateNumber(row.getValue("numHolders"))}
      </div>
      <LuUsers2 className="size-4 text-muted" />
    </div>
  );
};

export const renderHoldersCell = (props: HoldersCellProps) => {
  return <HoldersCell {...props} />;
};

export const renderVolumeTableCell = ({
  publicKey,
  volumeUsd,
  focusExchangeRate,
  desoExchangeRate,
}: {
  publicKey: string;
  volumeUsd: number;
  focusExchangeRate: number;
  desoExchangeRate: number;
}) => {
  return (
    <div className="flex flex-col gap-0 text-left font-mono text-xs">
      <div
        className={cn(
          "font-mono text-sm",
          volumeUsd === 0 ? "text-muted" : "text-foreground",
        )}
      >
        ${abbreviateNumber(volumeUsd)}
      </div>

      {renderDenominatingCurrencyTableCell({
        publicKey,
        focusExchangeRate,
        desoExchangeRate,
        priceUsd: volumeUsd,
      })}
    </div>
  );
};

export const renderCirculatingSupplyTableCell = ({
  circulatingSupply,
  totalSupply,
}: {
  circulatingSupply: number;
  totalSupply: number;
}) => {
  const circulatingSupplyPercentage = Number(
    formatDecimalValue((Number(circulatingSupply) * 100) / Number(totalSupply)),
  );
  const percentage = Number.isNaN(circulatingSupplyPercentage)
    ? 0
    : circulatingSupplyPercentage;

  return (
    <div className="flex w-full flex-col gap-0 font-mono text-xs">
      <div className="text-left font-mono text-sm text-foreground">
        {abbreviateNumber(circulatingSupply)}
      </div>
      <div className="flex flex-row items-center gap-2 text-left">
        <Progress
          indicatorClassName="bg-muted-foreground"
          value={percentage}
          className="h-1 max-w-[60%] bg-card"
        />
        <span className="text-[10px] text-muted">{percentage}%</span>
      </div>
    </div>
  );
};

export const renderCirculatingSupplyTooltip = () => {
  return (
    <InfoTooltip
      iconSize={16}
      text="This is the total number of coins in circulation, including locked coins."
    />
  );
};

export const renderApyTooltip = () => {
  return (
    <InfoTooltip
      iconSize={16}
      text='This column shows the APY (annual percentage yield) you would get for
  locking your coins for exactly one year. You can lock your coins by
  hitting the three dots and then hitting "Lock."'
    />
  );
};
